"use client";

import { type RawGameData, transformGameData } from "@/lib/games";
import {
  fetchGamesInIdChunks,
  getPubliclyListedGameIds,
} from "@/lib/games/fetch-publicly-listed-games";
import { createClient } from "@/lib/supabase/client";
import type { GameWithRelations } from "@/types/game";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys for consistent caching
export const gameKeys = {
  all: ["games"] as const,
  lists: () => [...gameKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...gameKeys.lists(), { filters }] as const,
  details: () => [...gameKeys.all, "detail"] as const,
  detail: (id: string) => [...gameKeys.details(), id] as const,
  featured: () => [...gameKeys.all, "featured"] as const,
  related: (gameId: string) => [...gameKeys.all, "related", gameId] as const,
  admin: () => [...gameKeys.all, "admin"] as const,
};

/** Columns needed for `GameCard` + masonry grid on game detail; avoids heavy tag/publisher joins. */
const GAMES_CARDS_GRID_SELECT = [
  "id",
  "name",
  "slug",
  "size",
  "new_tag",
  "thumbnail_url",
  "preview_video_url",
  "sort_order",
  "desktop_only",
  "published",
  "archived",
].join(", ");

const GAMES_LIST_FULL_KEY = { variant: "full" } as const;
const GAMES_LIST_CARD_KEY = { variant: "cardSummary" } as const;

export type UseGamesOptions = {
  /** When false, the query does not run (e.g. server already supplied listing data). */
  enabled?: boolean;
  /**
   * - `full`: default list query with tags and publishers
   * - `cardSummary`: only fields used by `GameCard` / `GamesGrid` to reduce Supabase egress
   */
  variant?: "full" | "cardSummary";
};

// Fetch all published games
export function useGames(options?: UseGamesOptions) {
  const { enabled = true, variant = "full" } = options ?? {};
  const supabase = createClient();
  const listKey = variant === "cardSummary" ? GAMES_LIST_CARD_KEY : GAMES_LIST_FULL_KEY;

  return useQuery({
    queryKey: [...gameKeys.lists(), listKey],
    queryFn: async (): Promise<GameWithRelations[]> => {
      const select =
        variant === "cardSummary"
          ? GAMES_CARDS_GRID_SELECT
          : `
          *,
          tags:game_tags(
            tag:tags(id, name, slug, description, display_order, created_at, updated_at)
          ),
          publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url),
          publishers:game_publishers(
            publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url)
          )
        `;

      const listedIds = await getPubliclyListedGameIds(supabase);

      const data = await fetchGamesInIdChunks<RawGameData>(
        listedIds,
        (chunk) =>
          supabase
            .from("games")
            .select(select)
            .in("id", chunk)
            .eq("published", true)
            .eq("archived", false)
            .order("sort_order", { ascending: true }),
        "Failed to fetch games"
      );

      data.sort(
        (left, right) =>
          (left.sort_order ?? Number.MAX_SAFE_INTEGER) -
          (right.sort_order ?? Number.MAX_SAFE_INTEGER)
      );

      return transformGameData(data);
    },
    enabled,
  });
}

// Fetch single game by slug
export function useGame(slug: string) {
  const supabase = createClient();

  return useQuery({
    queryKey: gameKeys.detail(slug),
    queryFn: async (): Promise<GameWithRelations | null> => {
      const { data, error } = await supabase
        .from("games")
        .select(`
          *,
          tags:game_tags(
            tag:tags(id, name, slug, description, display_order, created_at, updated_at)
          ),
          publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url),
          publishers:game_publishers(
            publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url)
          )
        `)
        .eq("slug", slug)
        .eq("archived", false)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return null; // Game not found
        }
        throw new Error(`Failed to fetch game: ${error.message}`);
      }

      if (!data) return null;

      // Transform the single game data
      const transformedData = transformGameData([data]);
      return transformedData[0] || null;
    },
    enabled: !!slug,
  });
}

// Fetch featured games
export function useFeaturedGames(limit = 6) {
  const supabase = createClient();

  return useQuery({
    queryKey: [...gameKeys.featured(), { limit }],
    queryFn: async (): Promise<GameWithRelations[]> => {
      const { data, error } = await supabase
        .from("games")
        .select(`
          *,
          tags:game_tags(
            tag:tags(id, name, slug, description, display_order, created_at, updated_at)
          ),
          publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url),
          publishers:game_publishers(
            publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url)
          )
        `)
        .eq("published", true)
        .eq("archived", false)
        .order("sort_order", { ascending: true })
        .limit(limit);

      if (error) {
        throw new Error(`Failed to fetch featured games: ${error.message}`);
      }

      return transformGameData(data);
    },
  });
}

// Helper function to get current game metadata
async function getCurrentGameMetadata(supabase: SupabaseClient, gameId: string) {
  const { data: currentGame, error: currentError } = await supabase
    .from("games")
    .select(`
      game_tags(tag_id),
      game_publishers(publisher_id)
    `)
    .eq("id", gameId)
    .single();

  if (currentError || !currentGame) {
    return null;
  }

  return {
    tagIds: currentGame.game_tags?.map((gt: { tag_id: number }) => gt.tag_id) || [],
    publisherIds:
      currentGame.game_publishers?.map((gp: { publisher_id: number }) => gp.publisher_id) || [],
  };
}

// Helper function to get game IDs by tags
async function getGameIdsByTags(supabase: SupabaseClient, tagIds: number[]) {
  const { data: gameIdsData, error: gameIdsError } = await supabase
    .from("game_tags")
    .select("game_id")
    .in("tag_id", tagIds);

  if (gameIdsError) {
    console.warn("Failed to fetch game IDs by tags:", gameIdsError.message);
    return [];
  }

  return gameIdsData?.map((item: { game_id: string }) => item.game_id) || [];
}

// Helper function to get game IDs by publishers
async function getGameIdsByPublishers(supabase: SupabaseClient, publisherIds: number[]) {
  const { data: gameIdsData, error: gameIdsError } = await supabase
    .from("game_publishers")
    .select("game_id")
    .in("publisher_id", publisherIds);

  if (gameIdsError) {
    console.warn("Failed to fetch game IDs by publishers:", gameIdsError.message);
    return [];
  }

  return gameIdsData?.map((item: { game_id: string }) => item.game_id) || [];
}

// Helper function to create base query
function createRelatedGamesQuery(supabase: SupabaseClient, gameId: string, limit: number) {
  return supabase
    .from("games")
    .select(`
      *,
      tags:game_tags(
        tag:tags(id, name, slug, description, display_order, created_at, updated_at)
      ),
      publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url),
      publishers:game_publishers(
        publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url)
      )
    `)
    .eq("published", true)
    .eq("archived", false)
    .neq("id", gameId)
    .limit(limit);
}

// Helper function to process and add games to collection
function addGamesToCollection(
  games: GameWithRelations[],
  collectedGames: GameWithRelations[],
  usedGameIds: Set<string>,
  limit: number
) {
  for (const game of games) {
    if (collectedGames.length >= limit) break;
    if (!usedGameIds.has(game.id)) {
      collectedGames.push(game);
      usedGameIds.add(game.id);
    }
  }
}

// Generic helper function to collect games by any criteria
async function collectGames(
  supabase: SupabaseClient,
  gameId: string,
  ids: number[],
  limit: number,
  collectedGames: GameWithRelations[],
  usedGameIds: Set<string>,
  getGameIdsFunction: (supabase: SupabaseClient, ids: number[]) => Promise<string[]>,
  errorMessage: string
) {
  if (ids.length === 0 || collectedGames.length >= limit) return;

  const gameIds = await getGameIdsFunction(supabase, ids);
  if (gameIds.length === 0) return;

  const { data, error } = await createRelatedGamesQuery(supabase, gameId, limit).in("id", gameIds);

  if (error) {
    console.warn(errorMessage, error.message);
    return;
  }

  if (data && data.length > 0) {
    const transformedGames = transformGameData(data);
    addGamesToCollection(transformedGames, collectedGames, usedGameIds, limit);
  }
}

// Simplified helper function to collect games by tags
async function collectGamesByTags(
  supabase: SupabaseClient,
  gameId: string,
  tagIds: number[],
  limit: number,
  collectedGames: GameWithRelations[],
  usedGameIds: Set<string>
) {
  await collectGames(
    supabase,
    gameId,
    tagIds,
    limit,
    collectedGames,
    usedGameIds,
    getGameIdsByTags,
    "Failed to fetch games by tags:"
  );
}

// Simplified helper function to collect games by publishers
async function collectGamesByPublishers(
  supabase: SupabaseClient,
  gameId: string,
  publisherIds: number[],
  limit: number,
  collectedGames: GameWithRelations[],
  usedGameIds: Set<string>
) {
  await collectGames(
    supabase,
    gameId,
    publisherIds,
    limit,
    collectedGames,
    usedGameIds,
    getGameIdsByPublishers,
    "Failed to fetch games by publishers:"
  );
}

// Helper function to collect random games
async function collectRandomGames(
  supabase: SupabaseClient,
  gameId: string,
  limit: number,
  collectedGames: GameWithRelations[],
  usedGameIds: Set<string>
) {
  if (collectedGames.length >= limit) return;

  const { data, error } = await createRelatedGamesQuery(supabase, gameId, limit * 2);
  if (error) {
    console.warn("Failed to fetch fallback games:", error.message);
    return;
  }

  if (data && data.length > 0) {
    const randomGames = transformGameData(data);
    addGamesToCollection(randomGames, collectedGames, usedGameIds, limit);
  }
}

// Fetch related games
export function useRelatedGames(gameId: string, limit = 20) {
  const supabase = createClient();

  return useQuery({
    queryKey: [...gameKeys.related(gameId), { limit }],
    queryFn: async (): Promise<GameWithRelations[]> => {
      const metadata = await getCurrentGameMetadata(supabase, gameId);
      if (!metadata) {
        // If we can't get metadata, fallback to random games
        const { data, error } = await createRelatedGamesQuery(supabase, gameId, limit);
        if (error) {
          console.warn("Failed to fetch fallback games:", error.message);
          return [];
        }
        return transformGameData(data);
      }

      const { tagIds, publisherIds } = metadata;
      const collectedGames: GameWithRelations[] = [];
      const usedGameIds = new Set<string>();

      // Strategy 1: Try to find games by shared tags
      await collectGamesByTags(supabase, gameId, tagIds, limit, collectedGames, usedGameIds);

      // Strategy 2: Try to find games by shared publishers (if we need more games)
      await collectGamesByPublishers(
        supabase,
        gameId,
        publisherIds,
        limit,
        collectedGames,
        usedGameIds
      );

      // Strategy 3: Fill remaining slots with random games
      await collectRandomGames(supabase, gameId, limit, collectedGames, usedGameIds);

      return collectedGames;
    },
    enabled: !!gameId,
  });
}

// Admin: Fetch all games (including unpublished)
export function useAdminGames() {
  const supabase = createClient();

  return useQuery({
    queryKey: gameKeys.admin(),
    queryFn: async (): Promise<GameWithRelations[]> => {
      const { data, error } = await supabase
        .from("games")
        .select(`
          *,
          tags:game_tags(
            tag:tags(id, name, slug, description, display_order, created_at, updated_at)
          ),
          publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url),
          publishers:game_publishers(
            publisher:publishers(id, name, slug, created_at, updated_at, logo_url, website_url)
          )
        `)
        .order("sort_order", { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch admin games: ${error.message}`);
      }

      return transformGameData(data);
    },
  });
}

// Mutation: Update game sort orders
export function useUpdateGameSortOrders() {
  const queryClient = useQueryClient();
  const supabase = createClient();

  return useMutation({
    mutationFn: async (games: Array<{ id: string; sort_order: number }>) => {
      const { error } = await supabase.from("games").upsert(games, { onConflict: "id" });

      if (error) {
        throw new Error(`Failed to update game sort orders: ${error.message}`);
      }
    },
    onSuccess: () => {
      // Invalidate games queries to refetch with new order
      queryClient.invalidateQueries({ queryKey: gameKeys.all });
    },
  });
}
